/* eslint-disable object-curly-newline */
const { ACTION_TYPES: TYPES } = require('reducers/product');
const { buildProductHeroImageSrc, buildProductImageSrc, getMediaItems } = require('utils/product');
const {
    IMAGE_SIZES: { ZOOM }
} = require('utils/BCC');
const { LINK_TRACKING_EVENT } = require('analytics/constants');
const {
    MEDIA_TYPE: { IMAGE }
} = require('analytics/constants');
const { skuVariationType } = require('utils/Sku');
const AnalyticsUtils = require('analytics/utils');
const getProductDetails = require('services/api/search-n-browse/getProductDetails');
const localeUtils = require('utils/LanguageLocale');
const processEvent = require('analytics/processEvent');
const productPageLinkTracking = require('analytics/bindings/pages/product/productPageLinkTracking');
const ProfileApi = require('services/api/profile');
const skuUtils = require('utils/Sku');
const SnbApi = require('services/api/search-n-browse');
const store = require('Store');
const { THUMBNAIL_PRODUCT_MEDIA_ITEM } = require('style/imageSizes');

const { LINK_INFO, SPEC_EVENT_NAME } = productPageLinkTracking;

const SKU_UPDATE_SOURCE = {
    SWATCHES: 'SWATCHES',
    QUERY_STRING: 'QUERY_STRING'
};
const { SWATCHES } = SKU_UPDATE_SOURCE;

const anaConsts = require('analytics/constants');
const { PostLoad } = require('constants/events');
const { productViewableImpressionTrackingInformation, productClickTrackingInformation } = require('utils/product');
const anaUtils = require('analytics/utils');
const { COMPONENT_TITLE } = require('analytics/constants');
const { PRODUCTS_GRID } = COMPONENT_TITLE;

const getImagesToPreload = product => {
    const mediaItems = getMediaItems(product);
    const imagesToPreload = [];
    const size = THUMBNAIL_PRODUCT_MEDIA_ITEM;
    const hideBadge = true;
    const generateSrcs = true;
    mediaItems.forEach(item => {
        if (item.type === IMAGE) {
            let [x1PixelDensityUrl, x2PixelDensityUrl] = buildProductHeroImageSrc(item);
            imagesToPreload.push({
                x1PixelDensityUrl,
                x2PixelDensityUrl
            });

            const src = skuUtils.getImgSrc(ZOOM, item.media);
            [, , x1PixelDensityUrl, x2PixelDensityUrl] = buildProductImageSrc({
                src,
                size,
                hideBadge,
                generateSrcs
            });
            imagesToPreload.push({
                x1PixelDensityUrl,
                x2PixelDensityUrl
            });
        }
    });

    return imagesToPreload;
};

const ProductActions = {
    TYPES,

    SKU_UPDATE_SOURCE,

    isNewPage: ({ newLocation, previousLocation }) => {
        const productPageData = skuUtils.getProductPageData(newLocation);
        const previousProductPageData = skuUtils.getProductPageData(previousLocation);
        const differentProduct = productPageData.productId !== previousProductPageData?.productId;

        return differentProduct;
    },

    openPage:
        ({ newLocation, requestConfig, events }) =>
            dispatch => {
                const { onDataLoaded, onPageUpdated, onError } = events;

                try {
                    const productPageData = skuUtils.getProductPageData(newLocation);
                    const { productId, skuId } = productPageData;
                    const productPromise = SnbApi.getProductDetails(
                        productId,
                        skuId,
                        {
                            addCurrentSkuToProductChildSkus: true,
                            includeRegionsMap: true,
                            showContent: true,
                            includeConfigurableSku: true,
                            countryCode: localeUtils.isCanada() ? localeUtils.COUNTRIES.CA : localeUtils.COUNTRIES.US,
                            removePersonalizedData: true,
                            includeReviewFilters: true,
                            includeReviewImages: true
                        },
                        requestConfig
                    );
                    // TODO: SnbApi.getProductDetails ^^^ could return all data we need
                    // to skip invocation of ProfileApi.getUserSpecificProductDetails
                    const userSpecificProductDetailsPromise = ProfileApi.getUserSpecificProductDetails(productId, skuId);

                    return productPromise
                        .then(product => {
                            const imagesToPreload = getImagesToPreload(product);
                            onDataLoaded(product, imagesToPreload);
                            const setProductAction = ProductActions.setProduct(product);
                            dispatch(setProductAction); // Does +1 render circle on PDP

                            onPageUpdated(product, () => {
                                userSpecificProductDetailsPromise.then(userSpecificDetails => {
                                    const updateCurrentProductWithUserSpecificDetailsAction =
                                    ProductActions.updateCurrentUserSpecificProduct(userSpecificDetails);
                                    dispatch(updateCurrentProductWithUserSpecificDetailsAction); // Does +1 render circle on PDP
                                });
                            });
                        })
                        .catch(onError);
                } catch (error) {
                    onError(error);

                    return Promise.reject(error);
                }
            },

    updatePage:
        ({ newLocation, previousLocation }) =>
            dispatch => {
                const productPageData = skuUtils.getProductPageData(newLocation);
                const previousProductPageData = skuUtils.getProductPageData(previousLocation);
                // We are not switching product pages therefor we must be switching between skus on the same product page
                // We can assume that the sku is the default sku if it is missing from the product page data
                const skuId = (productPageData.skuId && productPageData.skuId[0]) || Sephora.productPage.defaultSkuId.toString();
                const sku = skuUtils.getSkuFromProduct(null, skuId);
                const prevSkuId = previousProductPageData.skuId && previousProductPageData.skuId[0];

                if (skuId && skuId !== (prevSkuId || Sephora.productPage.defaultSkuId.toString())) {
                    dispatch(ProductActions.updateSkuInCurrentProduct(sku, ProductActions.SKU_UPDATE_SOURCE.SWATCHES));
                }
            },

    updateProductWithUserSpecificData: () => dispatch => {
        const {
            page: {
                product,
                product: { currentProductUserSpecificDetails = {}, currentSku = {}, productDetails = {} }
            }
        } = store.getState();
        const productId = productDetails.productId || product.productId || currentProductUserSpecificDetails.productId;
        // TODO: temp fix while json product returns default sku instead of selected
        const skuId = AnalyticsUtils.getQueryParam('skuId') || currentSku.skuId;
        const promise = ProfileApi.getUserSpecificProductDetails(productId, skuId)
            .then(productData => dispatch(ProductActions.updateCurrentUserSpecificProduct({ ...productData })))
            .catch(() => {
                /* exception handling goes here */
            });

        return promise;
    },

    executeMergeActionSideEffects: (key, value) => {
        switch (key) {
            case 'hoveredSku': {
                const {
                    page: { product }
                } = store.getState();
                const hoveredSku = value;

                if (product.variationType === skuVariationType.COLOR && hoveredSku) {
                    const eventAgs = {
                        data: {
                            bindingMethods: [productPageLinkTracking.pageHoverColorSwatchBindings],
                            specificEventName: SPEC_EVENT_NAME.HOVER
                        }
                    };
                    processEvent.processOnce(LINK_TRACKING_EVENT, eventAgs, { actionInfo: LINK_INFO.HOVER });
                }

                break;
            }
            default: {
                // Should do nothing always!
                break;
            }
        }
    },

    changeCurrentSku: function (sku) {
        return {
            type: TYPES.CHANGE_CURRENT_SKU,
            currentSku: sku
        };
    },

    setProduct: product => ({
        type: TYPES.SET_PRODUCT,
        payload: { product }
    }),

    updateCurrentProduct: function (currentProduct) {
        return {
            type: TYPES.UPDATE_CURRENT_PRODUCT,
            currentProduct: currentProduct
        };
    },

    /** Mixes the user specific data in to the product (on all sku levels) and updates
     * the product in the store
     * @param {Object} currentProductUserSpecificDetails User product specific details
     * @returns {{type: string, payload: Object}} Action
     */
    updateCurrentUserSpecificProduct: currentProductUserSpecificDetails => ({
        type: TYPES.UPDATE_CURRENT_PRODUCT_USER_SPECIFIC,
        payload: { currentProductUserSpecificDetails }
    }),

    updateReserveOnlinePickUpInStoreProductDetails: function (updateReserveOnlinePickUpInStoreProductDetails) {
        return {
            type: TYPES.UPDATE_RESERVE_ONLINE_PICK_UP_IN_STORE_DETAILS,
            reserveOnlinePickUpInStoreProductDetails: updateReserveOnlinePickUpInStoreProductDetails
        };
    },

    restoreProductWithNoExtraDetails: function () {
        return { type: TYPES.RESTORE_PRODUCT_WITH_NO_EXTRA_DETAILS };
    },

    updateSkuInCurrentProduct: function (currentSku, source) {
        if (source === SWATCHES) {
            const {
                page: { product: currentProduct }
            } = store.getState();
            const specificEventName = currentSku.variationType?.includes(skuUtils.skuVariationType.SIZE)
                ? SPEC_EVENT_NAME.SIZE
                : SPEC_EVENT_NAME.CLICK;
            const eventArgs = {
                data: {
                    currentProduct,
                    bindingMethods: [productPageLinkTracking.pageClickBindings],
                    specificEventName,
                    sku: currentSku
                }
            };
            processEvent.process(LINK_TRACKING_EVENT, eventArgs);
        }

        return {
            type: TYPES.UPDATE_CURRENT_SKU_IN_CURRENT_PRODUCT,
            payload: { currentSku }
        };
    },

    updateSameDayDeliveryProductDetails: function (updateSameDayDeliveryProductDetails) {
        return {
            type: TYPES.UPDATE_SAME_DAY_DELIVERY_DETAILS,
            sameDayDeliveryProductDetails: updateSameDayDeliveryProductDetails
        };
    },

    toggleCustomSets: function (isOpen) {
        return {
            type: TYPES.TOGGLE_CUSTOM_SETS,
            isOpen: isOpen
        };
    },

    // forceapply tells ReviewsFilters to update, even if
    // filters have not changed
    applyReviewFilters: function (filters, forceApply = false) {
        return {
            type: TYPES.REVIEW_FILTERS_APPLIED,
            filters: filters,
            apply: forceApply
        };
    },

    selectReviewFilters: function (filters) {
        return {
            type: TYPES.REVIEW_FILTERS_SELECTED,
            filters: filters
        };
    },

    applyReviewsSearch: function (productId, keyword) {
        return {
            type: TYPES.REVIEW_SEARCH_APPLIED,
            productId,
            keyword
        };
    },

    beautyMatchFiltersToggled: function (filters, isChecked, name) {
        return {
            type: TYPES.BEAUTY_MATCH_FILTERS_TOGGLED,
            filters: filters,
            isChecked: isChecked,
            name: name
        };
    },

    verifiedPurchaserCheckboxToggled: function (filters, isChecked, name) {
        return {
            type: TYPES.VERIFIED_PURCHASER_FILTERS_TOGGLED,
            filters: filters,
            isChecked: isChecked,
            name: name
        };
    },

    applyGalleryFilters: function (filters, isBeautyMatch) {
        return {
            type: TYPES.GALLERY_FILTERS_APPLIED,
            filters: filters,
            isBeautyMatch: isBeautyMatch
        };
    },

    resetGalleryFilters: function () {
        return {
            type: TYPES.RESET_GALLERY_FILTERS,
            filters: []
        };
    },

    fetchCurrentProduct: function (productId, skuId) {
        return dispatch => {
            return getProductDetails(productId, skuId, { addCurrentSkuToProductChildSkus: true }).then(data =>
                dispatch(this.updateCurrentProduct(data))
            );
        };
    },

    toggleSwatches: function (isExpand) {
        const eventArgs = {
            data: {
                isExpand,
                bindingMethods: [productPageLinkTracking.pageToggleColorSwatchBindings]
            }
        };
        processEvent.process(LINK_TRACKING_EVENT, eventArgs);

        return {
            type: TYPES.TOGGLE_SWATCHES,
            payload: { isExpand }
        };
    },

    selectSortOption: function (code, name) {
        return {
            type: TYPES.SELECT_SORT_OPTION,
            sortOption: {
                code: code,
                name: name
            }
        };
    },

    selectFilterOption: function (code, name) {
        return {
            type: TYPES.SELECT_FILTER_OPTION,
            filterOption: {
                code: code,
                name: name
            }
        };
    },

    purchasesFilterOptions: function (filterOptions) {
        return {
            type: TYPES.PURCHASES_FILTER_OPTIONS,
            filterOptions: filterOptions
        };
    },

    fireProductViewableImpressionTracking: function (product) {
        // Async Action

        // Inside the Action Creator
        return () => {
            // Inside the Action
            if (!product || !product.sponsored) {
                return Promise.resolve();
            }

            const Events = require('utils/framework/Events');

            // Waits until the page is completely loaded to fire the event for Signal/TMS.
            Events.onLastLoadEvent(window, [PostLoad], () => {
                // Extras the viewable tracking information from the sponsored product
                const trackingInformation = productViewableImpressionTrackingInformation(product);

                // Verifies the sponsored product information is available and that the product is a sponsored one.
                if (trackingInformation && trackingInformation.isSponsoredProduct) {
                    processEvent.process(
                        anaConsts.LINK_TRACKING_EVENT,
                        {
                            data: {
                                linkName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_VIEWABLE_IMPR,
                                actionInfo: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_VIEWABLE_IMPR,
                                specificEventName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_VIEWABLE_IMPR,
                                sponsoredProductInformation: trackingInformation
                            }
                        },
                        { specificEventName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_VIEWABLE_IMPR }
                    );
                }
            });

            return Promise.resolve();
        };
    },

    fireSponsoredProductClickTracking: function (product, source, _event, index) {
        // Inside the Action Creator
        return () => {
            digitalData.page.attributes.previousPageData.pageType = source;
            // reset internalCampaign so `getInternalCampaign` in generalBindings will set it
            // to the icid2 queryParam in the pPage URL
            digitalData.page.attributes.previousPageData.internalCampaign = '';
            const navigationInfo = digitalData.page.attributes.previousPageData.navigationInfo;
            const isLeftNav = navigationInfo.startsWith('left');

            if (!product || !product.sponsored) {
                return Promise.resolve();
            }

            const Events = require('utils/framework/Events');

            // Waits until the page is completely loaded to fire the event for Signal/TMS.
            Events.onLastLoadEvent(window, [PostLoad], () => {
                const trackingInformation = productClickTrackingInformation(product);

                // Verifies the sponsored product information is available and that the product is a sponsored one.
                if (trackingInformation && trackingInformation.isSponsoredProduct) {
                    processEvent.process(
                        anaConsts.LINK_TRACKING_EVENT,
                        {
                            data: {
                                linkName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_CLICK,
                                actionInfo: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_CLICK,
                                specificEventName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_CLICK,
                                sponsoredProductInformation: trackingInformation,
                                eventStrings: anaConsts.Event.EVENT_254,
                                internalCampaign: anaConsts.CAMPAIGN_STRINGS.RMN_PLA,
                                productStrings: `;${product.currentSku?.skuId};;;${anaConsts.Event.EVENT_254};eVar26=${product.currentSku?.skuId}|eVar124=N/A:[${index}]:N/A`
                            }
                        },
                        { specificEventName: anaConsts.EVENT_NAMES.PLA_SPONSORED_PRODUCT_CLICK }
                    );
                }
            });

            // digitalData.page.attributes.previousPageData.recInfo is reset on SPA page loads
            anaUtils.setNextPageData({
                recInfo: { componentTitle: PRODUCTS_GRID },
                navigationInfo: isLeftNav ? null : navigationInfo
            });

            return Promise.resolve();
        };
    }
};

module.exports = ProductActions;
